/**
 * The files this client uploaded for sharing, kept so its own stage can show
 * them without downloading them back.
 *
 * A presenter's browser used to fetch the whole file it had just sent, from
 * the server, before it could show page 1: on a slow line the presenter waited
 * longest for their own document. The picked File is already in memory, so the
 * stage uses it. Everyone else still loads the uploaded copy, which holds the
 * same bytes, so pages and annotations line up.
 *
 * Keyed by the uploaded URL, the one broadcast with the share and the one the
 * stage is handed back. A PowerPoint, Word or text file presents its server-made
 * PDF copy under another URL, so it finds nothing here and downloads as before.
 */

/** Past this, the oldest copies go: they are only a shortcut. */
const MAX_TOTAL_BYTES = 200 * 1024 * 1024

const copies = new Map<string, Blob>()
let totalBytes = 0

/** Remember `file` as the local copy of what was uploaded to `url`. */
export function keepLocalCopy(url: string, file: Blob): void {
  dropLocalCopy(url)
  if (file.size > MAX_TOTAL_BYTES) return
  copies.set(url, file)
  totalBytes += file.size
  // A Map iterates in insertion order, so the first key is the oldest.
  for (const [oldest, blob] of copies) {
    if (totalBytes <= MAX_TOTAL_BYTES) break
    copies.delete(oldest)
    totalBytes -= blob.size
  }
}

/** The local copy of what was uploaded to `url`, if this client still has it. */
export function localCopyOf(url: string | null | undefined): Blob | undefined {
  return url ? copies.get(url) : undefined
}

/** Forget one copy, when its file is deleted. */
export function dropLocalCopy(url: string | null | undefined): void {
  if (!url) return
  const blob = copies.get(url)
  if (!blob) return
  copies.delete(url)
  totalBytes -= blob.size
}

/** Forget them all, when the meeting is left. */
export function dropAllLocalCopies(): void {
  copies.clear()
  totalBytes = 0
}
